import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { getTypeInfo } from 'credit-card-type';

import { cn } from 'helpers/classnames';
import MartyLink from 'components/common/MartyLink';
import DeleteOverlay from 'components/account/InfoCard/DeleteOverlay';
import { isCreditCardExpired, normalizePaymentType } from 'helpers/MyAccountUtils';
import { track } from 'apis/amethyst';
import { evEditPaymentClick } from 'events/account';
import marketplace from 'cfg/marketplace.json';

import css from 'styles/components/account/paymentCard.scss';
import cardCss from 'styles/components/account/infoCard/infoCard.scss';

class PaymentCard extends Component {
  state = {
    showConfirmation: false
  };

  showDeleteConfirmation = () => {
    this.setState({ showConfirmation: true });
  };

  hideDeleteConfirmation = () => {
    this.setState({ showConfirmation: false });
  };

  onEditClick = e => {
    track(() => [
      evEditPaymentClick,
      {
        paymentInstrumentId: e.currentTarget.dataset['paymentId']
      }
    ]);
  };

  onDeleteClick = () => {
    const { card, onDelete } = this.props;
    onDelete(card);
  };

  onMakePrimaryClick = () => {
    const { card, onPrimary } = this.props;
    onPrimary(card);
  };

  makeDeleteOverlay = () => {
    const { showConfirmation } = this.state;

    return (
      showConfirmation && (
        <DeleteOverlay cardInfoText="payment method" hideDeleteConfirmation={this.hideDeleteConfirmation} onDeleteClick={this.onDeleteClick} />
      )
    );
  };

  makeBillingAddress = () => {
    const {
      card: { billingAddress }
    } = this.props;
    const { testId } = this.context;
    if (billingAddress && Object.keys(billingAddress).length) {
      return (
        <div>
          <div data-test-id={testId('billingAddressLine1')}>{billingAddress.addressLine1}</div>
          <div data-test-id={testId('billingAddressLine2')}>{billingAddress.addressLine2}</div>
          <div data-test-id={testId('billingAddressCityStateZip')}>
            {billingAddress.city}, {billingAddress.stateOrRegion} {billingAddress.postalCode}
          </div>
        </div>
      );
    } else {
      return <div data-test-id={testId('noBillingAddressSelected')}>No billing address selected</div>;
    }
  };

  render() {
    const { testId } = this.context;
    const { card } = this.props;
    const { addCreditCardNumber, paymentInstrumentId, fullName, expirationMonth, expirationYear, primary, paymentType } = card;
    const isExpired = isCreditCardExpired(card);
    const cardType = getTypeInfo(normalizePaymentType(paymentType))?.niceType;
    const ariaPaymentCardId = `aria-paymentCard-${paymentInstrumentId}`;

    return (
      <li
        className={cn(cardCss.card, { [cardCss.defaultCard]: primary })}
        data-cs-mask={marketplace.dataMaskPiiElements}
        data-test-id={testId('paymentCard')}
      >
        <div id={ariaPaymentCardId} className={cn(cardCss.cardDetails, 'zappos:body-s')}>
          <div className={css.creditCard}>
            <div data-test-id={testId('paymentCardTypeLastFour')}>
              {cardType} ****{addCreditCardNumber}
            </div>
            <div className={cn({ [css.expired]: isExpired })} data-test-id={testId('expirationDate')}>
              Exp. {expirationMonth}/{expirationYear}
            </div>
          </div>
          <div>
            <div data-test-id={testId('billingAddressFullName')}>{fullName}</div>
            {this.makeBillingAddress()}
          </div>
        </div>
        <div className={cardCss.cardActions}>
          <div>
            <MartyLink
              to={`/payments/${paymentInstrumentId}/edit`}
              onClick={this.onEditClick}
              data-payment-id={paymentInstrumentId}
              data-test-id={testId('editPayment')}
              aria-describedby={ariaPaymentCardId}
            >
              Edit
            </MartyLink>
            <span className={cardCss.separator}>|</span>
            <button type="button" onClick={this.showDeleteConfirmation} data-test-id={testId('deletePayment')} aria-describedby={ariaPaymentCardId}>
              Delete
            </button>
            {!primary && (
              <>
                <span className={cardCss.separator}>|</span>
                <button
                  type="button"
                  onClick={this.onMakePrimaryClick}
                  data-test-id={testId('setAsPrimaryCard')}
                  aria-describedby={ariaPaymentCardId}
                >
                  Set as Primary
                </button>
              </>
            )}
          </div>
        </div>
        {this.makeDeleteOverlay()}
      </li>
    );
  }
}

PaymentCard.contextTypes = {
  testId: PropTypes.func
};

PaymentCard.propTypes = {
  card: PropTypes.object.isRequired,
  onPrimary: PropTypes.func,
  onDelete: PropTypes.func
};

export default PaymentCard;
